/** Parffragrance quiz v5: background catalogue loader and product parser. */
(() => {
  'use strict';
  const Q = window.ParfQuizV5;
  if (!Q) return;
  const { PRIVATE_COLLECTION_HANDLE, PAGE_SIZE, MAX_CATALOG_PAGES, INTENSE_TERMS, NON_PERFUME_TERMS, FAMILY_ALIASES, QUESTIONS } = Q;

  Q.CatalogFinder = class ParfQuizCatalogFinder {
    constructor(container) {
      if (!container || container.dataset.quizInitialised === 'true') return;
      container.dataset.quizInitialised = 'true';

      this.container = container;
      this.catalog = [];
      this.catalogStatus = 'idle';
      this.catalogError = null;
      this.catalogPromise = null;
      this.answers = {};
      this.currentQuestionIndex = 0;
      this.maxRecommendations = Math.max(3, Number(container.dataset.maxRecommendations) || 6);
      this.showPrices = container.dataset.showPrices !== 'false';
      this.currency = window.Shopify?.currency?.active || 'AED';

      this.states = Object.fromEntries(
        Array.from(container.querySelectorAll('[data-state]')).map((element) => [element.dataset.state, element])
      );

      this.elements = {
        start: container.querySelector('.quiz-start-btn'),
        status: container.querySelector('.quiz-load-status'),
        progress: container.querySelector('.quiz-progress'),
        progressFill: container.querySelector('.quiz-progress__fill'),
        currentQuestion: container.querySelector('.quiz-current-q'),
        totalQuestions: container.querySelector('.quiz-total-q'),
        questionTitle: container.querySelector('.quiz-question__title'),
        questionHelp: container.querySelector('.quiz-question__help'),
        options: container.querySelector('.quiz-question__options'),
        previous: container.querySelector('.quiz-nav__prev'),
        skip: container.querySelector('.quiz-nav__skip'),
        loadingText: container.querySelector('.quiz-loading__text'),
        resultsGrid: container.querySelector('.quiz-results__grid'),
        resultsFilters: container.querySelector('.quiz-results__filters-list'),
        noMatches: container.querySelector('.quiz-results__no-matches'),
        noMatchesTitle: container.querySelector('.quiz-results__no-matches h3'),
        noMatchesText: container.querySelector('.quiz-results__no-matches p'),
        noMatchesEdit: container.querySelector('.quiz-results__no-matches .quiz-results__edit'),
        noMatchesRetry: container.querySelector('.quiz-results__retry')
      };

      this.bindEvents();
      if (this.elements.totalQuestions) this.elements.totalQuestions.textContent = String(QUESTIONS.length);
      this.setLoadState('background');
      this.scheduleCatalogLoad();
    }

    bindEvents() {
      this.elements.start?.addEventListener('click', () => this.start());
      this.elements.previous?.addEventListener('click', () => this.goBack());
      this.elements.skip?.addEventListener('click', () => this.skip());

      this.elements.options?.addEventListener('click', (event) => {
        const option = event.target.closest('.quiz-option');
        if (option) this.choose(Number(option.dataset.index));
      });

      this.elements.options?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const option = event.target.closest('.quiz-option');
        if (!option) return;
        event.preventDefault();
        this.choose(Number(option.dataset.index));
      });

      this.elements.resultsGrid?.addEventListener('click', (event) => {
        const button = event.target.closest('.quiz-product__quick-add');
        if (button) this.quickAdd(button);
      });

      this.container.querySelectorAll('.quiz-results__retake').forEach((button) => {
        button.addEventListener('click', () => this.start(true));
      });

      this.container.querySelectorAll('.quiz-results__edit').forEach((button) => {
        button.addEventListener('click', () => this.editAnswers());
      });

      this.elements.noMatchesRetry?.addEventListener('click', () => this.calculateResults(true));
    }

    scheduleCatalogLoad() {
      const begin = () => this.ensureCatalogLoading().catch(() => {});
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(begin, { timeout: 1200 });
      } else {
        window.setTimeout(begin, 450);
      }
    }

    ensureCatalogLoading(force = false) {
      if (this.catalogStatus === 'ready' && this.catalog.length && !force) {
        return Promise.resolve(this.catalog);
      }

      if (this.catalogPromise && !force) return this.catalogPromise;

      if (force) {
        this.catalog = [];
        this.catalogPromise = null;
        this.catalogError = null;
        this.catalogStatus = 'idle';
      }

      this.catalogStatus = 'loading';
      this.setLoadState('loading');
      this.catalogPromise = this.loadCatalog();
      return this.catalogPromise;
    }

    async loadCatalog() {
      try {
        const [allProducts, privateProducts] = await Promise.all([
          this.fetchCollectionProducts('all'),
          this.fetchCollectionProducts(PRIVATE_COLLECTION_HANDLE)
        ]);

        const privateHandles = new Set(privateProducts.map((product) => product.handle));
        const merged = new Map();

        [...allProducts, ...privateProducts].forEach((product) => {
          const existing = merged.get(product.handle);
          merged.set(product.handle, { ...existing, ...product });
        });

        this.catalog = Array.from(merged.values())
          .filter((product) => this.isPerfumeProduct(product, privateHandles.has(product.handle)))
          .flatMap((product) => this.buildProductCandidates(product, privateHandles.has(product.handle)));

        if (!this.catalog.length) {
          throw new Error('No eligible perfume variants were returned by the storefront catalogue.');
        }

        this.catalogStatus = 'ready';
        this.catalogError = null;
        this.setLoadState('ready');
        return this.catalog;
      } catch (error) {
        this.catalogStatus = 'error';
        this.catalogError = error;
        this.catalogPromise = null;
        console.error('[Parffragrance Scent Finder] Catalogue loading failed:', error);
        this.setLoadState('error');
        throw error;
      }
    }

    async fetchCollectionProducts(handle) {
      const products = [];

      for (let page = 1; page <= MAX_CATALOG_PAGES; page += 1) {
        const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${PAGE_SIZE}&page=${page}`;
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          if (handle === PRIVATE_COLLECTION_HANDLE && response.status === 404) {
            throw new Error('Private Collection could not be reached.');
          }
          throw new Error(`Catalogue request failed with status ${response.status}.`);
        }

        const payload = await response.json();
        const pageProducts = Array.isArray(payload.products) ? payload.products : [];
        products.push(...pageProducts);

        if (pageProducts.length < PAGE_SIZE) break;
      }

      return products;
    }

    isPerfumeProduct(product, isPrivate) {
      if (isPrivate) return true;
      const tags = this.normaliseTags(product.tags);
      const text = [
        product.title,
        product.handle,
        product.product_type,
        tags.join(' '),
        product.body_html
      ].filter(Boolean).join(' ').toLowerCase();

      if (NON_PERFUME_TERMS.test(text)) return false;
      return /perfume|parfum|fragrance/.test(text) || /main\s*scent|high\s*note|top\s*note|base\s*note/.test(text);
    }

    buildProductCandidates(product, isPrivate) {
      const tags = this.normaliseTags(product.tags);
      const gender = this.detectGender(tags, product.title);
      const scentData = this.parseScentData(product.body_html || '');
      const image = product.images?.[0]?.src || product.image?.src || '';
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const candidatesByIntensity = new Map();

      variants.forEach((variant) => {
        if (variant.available === false) return;

        const intensity = this.detectVariantIntensity(product, variant);
        const candidate = {
          id: String(product.id || product.handle),
          variantId: String(variant.id || ''),
          handle: product.handle,
          title: product.title,
          variantTitle: this.cleanVariantTitle(variant.title),
          price: Number(variant.price || 0),
          image,
          gender,
          intensity,
          collection: isPrivate ? 'private' : 'core',
          mainScent: scentData.mainScent,
          notes: scentData.notes,
          familyScores: this.classifyFamilies(product, scentData),
          canonicalKey: this.canonicalScentKey(product.title, product.handle),
          available: variant.available !== false
        };

        const existing = candidatesByIntensity.get(intensity);
        if (!existing || this.preferCandidate(candidate, existing)) {
          candidatesByIntensity.set(intensity, candidate);
        }
      });

      return Array.from(candidatesByIntensity.values());
    }

    detectVariantIntensity(product, variant) {
      const optionNames = Array.isArray(product.options)
        ? product.options.map((option) => option.name || '')
        : [];
      const optionValues = [variant.option1, variant.option2, variant.option3];

      for (let index = 0; index < optionNames.length; index += 1) {
        if (/intensity|concentration/i.test(optionNames[index])) {
          const value = String(optionValues[index] || '');
          return INTENSE_TERMS.test(value) ? 'intense' : 'regular';
        }
      }

      return INTENSE_TERMS.test(String(variant.title || '')) ? 'intense' : 'regular';
    }

    detectGender(tags, title) {
      const searchable = `${tags.join(' ')} ${title || ''}`.toLowerCase();
      if (/\b(unisex|genderless)\b/.test(searchable)) return 'unisex';
      if (/\b(women|woman|female|femme|for her|ladies)\b/.test(searchable)) return 'women';
      if (/\b(men|man|male|homme|for him|gentlemen)\b/.test(searchable)) return 'men';
      return 'unisex';
    }

    parseScentData(html) {
      const text = this.htmlToText(html).replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();

      const capture = (patterns) => {
        for (const pattern of patterns) {
          const match = text.match(pattern);
          if (match?.[1]) return this.splitNotes(match[1]);
        }
        return [];
      };

      const main = capture([/main\s*scent\s*:\s*(.*?)(?=\s*(?:high|top|middle|heart|base)\s*notes?\s*:|$)/i]);
      const high = capture([/(?:high|top|head)\s*notes?\s*:\s*(.*?)(?=\s*(?:middle|heart|base)\s*notes?\s*:|$)/i]);
      const middle = capture([/(?:middle|heart)\s*notes?\s*:\s*(.*?)(?=\s*base\s*notes?\s*:|$)/i]);
      const base = capture([/base\s*notes?\s*:\s*(.*?)$/i]);

      return {
        mainScent: main.join(' ') || '',
        notes: { high, middle, base }
      };
    }

    classifyFamilies(product, scentData) {
      const text = [
        product.title,
        scentData.mainScent,
        ...scentData.notes.high,
        ...scentData.notes.middle,
        ...scentData.notes.base
      ].join(' ').toLowerCase();

      const scores = {};

      Object.entries(FAMILY_ALIASES).forEach(([family, aliases]) => {
        scores[family] = aliases.reduce((total, alias) => {
          const expression = new RegExp(`\\b${this.escapeRegExp(alias)}\\b`, 'gi');
          const matches = text.match(expression);
          return total + (matches ? matches.length : 0);
        }, 0);
      });

      return scores;
    }

  };
})();
